import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatIsoDate } from '../../schedule/lib/format'
import { formatMoney, toEditText } from '../lib/money'
import type { CostInvoice } from '../types'
import DetailRow from '../components/DetailRow'

const DASH = '—'

interface Props { invoice: CostInvoice; projectId: string; currency: string; locale: string }

const InvoiceSummaryCard: React.FC<Props> = ({ invoice, projectId, currency, locale }) => {
  const { t } = useTranslation()
  const date = (v: string | null) => (v ? formatIsoDate(t, v) : DASH)
  const when = (name: string | null, at: string | null) => (at ? `${name ?? DASH} · ${new Date(at).toLocaleString(t('date_locale') || 'cs-CZ')}` : DASH)
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader><CardTitle className="text-base">{t('CostControl_invoice_details_title')}</CardTitle></CardHeader>
      <CardContent>
        <dl className="divide-y">
          <DetailRow label={t('CostControl_invoice_field_contract')}>
            <Link className="text-primary hover:underline" to={`/project/${projectId}/cost-control/contracts/${invoice.contractId}`}>
              {invoice.contractNumber ? `${invoice.contractNumber} · ${invoice.contractName}` : invoice.contractName}
            </Link>
          </DetailRow>
          <DetailRow label={t('CostControl_contract_field_counterparty')}>{invoice.counterpartyName}</DetailRow>
          <DetailRow label={t('CostControl_invoice_field_amount')}><strong className="tabular-nums">{formatMoney(invoice.amount, { locale, currency })}</strong></DetailRow>
          {invoice.retentionPercent !== null && (
            <>
              <DetailRow label={t('CostControl_invoice_field_retention', { percent: toEditText(invoice.retentionPercent, locale) })}>
                <span className="tabular-nums">{formatMoney(invoice.retention, { locale, currency })}</span>
              </DetailRow>
              <DetailRow label={t('CostControl_invoice_field_payable')}><strong className="tabular-nums">{formatMoney(invoice.payable, { locale, currency })}</strong></DetailRow>
            </>
          )}
          {invoice.applicationId && (
            <DetailRow label={t('CostControl_invoice_field_application')}>
              <Link className="text-primary hover:underline" to={`/project/${projectId}/cost-control/applications/${invoice.applicationId}`}>
                {t('CostControl_application_title', { number: invoice.applicationNumber })}
              </Link>
            </DetailRow>
          )}
          <DetailRow label={t('CostControl_invoice_field_issueDate')}>{date(invoice.issueDate)}</DetailRow>
          <DetailRow label={t('CostControl_invoice_field_dueDate')}>{date(invoice.dueDate)}</DetailRow>
          <DetailRow label={t('CostControl_invoice_field_paidAt')}>{date(invoice.paidAt)}</DetailRow>
          <DetailRow label={t('CostControl_invoice_field_approved')}>{when(invoice.approvedByName, invoice.approvedAt)}</DetailRow>
          <DetailRow label={t('CostControl_invoice_field_created')}>{when(invoice.createdByName, invoice.createdAt)}</DetailRow>
          <DetailRow label={t('CostControl_invoice_field_note')}><span className="whitespace-pre-wrap">{invoice.note || DASH}</span></DetailRow>
        </dl>
      </CardContent>
    </Card>
  )
}
export default InvoiceSummaryCard
